import { useState, useEffect } from "react";

import { Library, Code, FileText, Palette, BookOpen, Loader2, Plus, Check, CheckCircle2, Search, ArrowUpDown, ChevronDown, ChevronUp, Star } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "@/components/ui/sonner";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";

interface ConventionPack {
  id: string;
  name: string;
  description: string;
  category: string;
  conventions: string[];
  is_system: boolean;
}

interface ConventionPackLibraryProps {
  workspaceId?: string;
  boardId?: string;
  folderId?: string;
  tagId?: string;
  projectId?: string;
  scopeLabel: string;
}

const CATEGORY_CONFIG = {
  coding: { label: "Coding", icon: Code, color: "hsl(210, 100%, 60%)" },
  writing: { label: "Writing", icon: FileText, color: "hsl(280, 100%, 60%)" },
  documentation: { label: "Documentation", icon: BookOpen, color: "hsl(160, 100%, 40%)" },
  design: { label: "Design", icon: Palette, color: "hsl(340, 100%, 60%)" },
};

export const ConventionPackLibrary = ({
  workspaceId,
  boardId,
  folderId,
  tagId,
  projectId,
  scopeLabel,
}: ConventionPackLibraryProps) => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [packs, setPacks] = useState<ConventionPack[]>([]);
  const [loading, setLoading] = useState(true);
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState<"name" | "conventions" | "status" | "favorites">("favorites");
  const [enabling, setEnabling] = useState<string | null>(null);
  const [existingMemories, setExistingMemories] = useState<string[]>([]);
  const [enabledPacks, setEnabledPacks] = useState<Set<string>>(new Set());
  const [expandedPacks, setExpandedPacks] = useState<Set<string>>(new Set());
  const [favorites, setFavorites] = useState<Set<string>>(new Set());
  const [togglingFavorite, setTogglingFavorite] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      fetchPacks();
      fetchExistingMemories();
      fetchFavorites();
    }
  }, [open, workspaceId, boardId, folderId, tagId, projectId]);

  const fetchExistingMemories = async () => {
    try {
      let query = supabase
        .from("memories")
        .select("content")
        .eq("category", "conventions");

      if (workspaceId) query = query.eq("workspace_id", workspaceId);
      else if (boardId) query = query.eq("board_id", boardId);
      else if (folderId) query = query.eq("folder_id", folderId);
      else if (tagId) query = query.eq("tag_id", tagId);
      else if (projectId) query = query.eq("project_id", projectId);

      const { data, error } = await query;

      if (error) throw error;
      
      const memoryContents = (data || []).map(m => m.content.trim().toLowerCase());
      setExistingMemories(memoryContents);
    } catch (error) {
      console.error("Error fetching existing memories:", error);
    }
  };

  const fetchFavorites = async () => {
    try {
      const { data, error } = await supabase
        .from("convention_pack_favorites")
        .select("pack_id");

      if (error) throw error;
      
      const favoriteIds = new Set((data || []).map(f => f.pack_id));
      setFavorites(favoriteIds);
    } catch (error) {
      console.error("Error fetching favorites:", error);
    }
  };

  const fetchPacks = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("convention_packs")
        .select("*")
        .order("category", { ascending: true })
        .order("name", { ascending: true });

      if (error) throw error;
      
      // Cast conventions from Json to string[]
      const typedData = (data || []).map(pack => ({
        ...pack,
        conventions: pack.conventions as unknown as string[]
      }));
      
      setPacks(typedData);
    } catch (error) {
      console.error("Error fetching convention packs:", error);
      toast.error("Failed to load convention packs");
    } finally {
      setLoading(false);
    }
  };

  const isPackEnabled = (pack: ConventionPack): boolean => {
    // Check if all conventions from the pack already exist
    const packConventions = pack.conventions.map(c => c.trim().toLowerCase());
    const matchCount = packConventions.filter(c => existingMemories.includes(c)).length;
    
    // Consider pack enabled if 80% or more of its conventions exist
    return matchCount >= packConventions.length * 0.8;
  };

  const getPackStatus = (pack: ConventionPack): { enabled: number; total: number } => {
    const packConventions = pack.conventions.map(c => c.trim().toLowerCase());
    const matchCount = packConventions.filter(c => existingMemories.includes(c)).length;
    
    return { enabled: matchCount, total: packConventions.length };
  };

  const handleEnablePack = async (pack: ConventionPack) => {
    setEnabling(pack.id);
    try {
      // Filter out conventions that already exist
      const existingSet = new Set(existingMemories);
      const newConventions = pack.conventions.filter(
        (convention) => !existingSet.has(convention.trim().toLowerCase())
      );

      if (newConventions.length === 0) {
        toast.info("All conventions from this pack are already enabled");
        setEnabling(null);
        return;
      }

      // Create a memory for each new convention
      const memoryInserts = newConventions.map((convention) => {
        const memoryData: any = {
          content: convention,
          category: "conventions",
          enabled: true,
        };

        if (workspaceId) memoryData.workspace_id = workspaceId;
        else if (boardId) memoryData.board_id = boardId;
        else if (folderId) memoryData.folder_id = folderId;
        else if (tagId) memoryData.tag_id = tagId;
        else if (projectId) memoryData.project_id = projectId;

        return memoryData;
      });

      const { error } = await supabase.from("memories").insert(memoryInserts);

      if (error) throw error;

      // Track usage
      const usageData: any = {
        pack_id: pack.id,
        user_id: user?.id,
        conventions_count: newConventions.length,
      };

      if (workspaceId) usageData.workspace_id = workspaceId;
      else if (boardId) usageData.board_id = boardId;
      else if (folderId) usageData.folder_id = folderId;
      else if (tagId) usageData.tag_id = tagId;
      else if (projectId) usageData.project_id = projectId;

      await supabase.from("convention_pack_usage").insert(usageData);

      const skipped = pack.conventions.length - newConventions.length;
      const message = skipped > 0
        ? `Enabled ${newConventions.length} new conventions (${skipped} already existed)`
        : `Enabled "${pack.name}" with ${newConventions.length} conventions`;

      toast.success(message);
      
      // Refresh existing memories
      await fetchExistingMemories();
    } catch (error) {
      console.error("Error enabling pack:", error);
      toast.error("Failed to enable convention pack");
    } finally {
      setEnabling(null);
    }
  };

  const togglePackExpansion = (packId: string) => {
    setExpandedPacks(prev => {
      const newSet = new Set(prev);
      if (newSet.has(packId)) {
        newSet.delete(packId);
      } else {
        newSet.add(packId);
      }
      return newSet;
    });
  };

  const toggleFavorite = async (packId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setTogglingFavorite(packId);
    
    try {
      const isFavorited = favorites.has(packId);
      
      if (isFavorited) {
        // Remove from favorites
        const { error } = await supabase
          .from("convention_pack_favorites")
          .delete()
          .eq("pack_id", packId)
          .eq("user_id", user?.id);
        
        if (error) throw error;
        
        setFavorites(prev => {
          const newSet = new Set(prev);
          newSet.delete(packId);
          return newSet;
        });
        
        toast.success("Removed from favorites");
      } else {
        // Add to favorites
        const { error } = await supabase
          .from("convention_pack_favorites")
          .insert({
            pack_id: packId,
            user_id: user?.id,
          });
        
        if (error) throw error;
        
        setFavorites(prev => new Set([...prev, packId]));
        toast.success("Added to favorites");
      }
    } catch (error) {
      console.error("Error toggling favorite:", error);
      toast.error("Failed to update favorite");
    } finally {
      setTogglingFavorite(null);
    }
  };

  const filteredAndSortedPacks = packs
    .filter((pack) => {
      // Category filter
      if (categoryFilter !== "all" && pack.category !== categoryFilter) return false;
      
      // Search filter
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase();
        const matchesName = pack.name.toLowerCase().includes(query);
        const matchesDescription = pack.description.toLowerCase().includes(query);
        const matchesConventions = pack.conventions.some(c => 
          c.toLowerCase().includes(query)
        );
        
        if (!matchesName && !matchesDescription && !matchesConventions) return false;
      }
      
      return true;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case "favorites":
          const aFav = favorites.has(a.id) ? 1 : 0;
          const bFav = favorites.has(b.id) ? 1 : 0;
          if (aFav !== bFav) return bFav - aFav; // Favorites first
          return a.name.localeCompare(b.name);
        case "conventions":
          return b.conventions.length - a.conventions.length;
        case "status":
          const aEnabled = isPackEnabled(a) ? 1 : 0;
          const bEnabled = isPackEnabled(b) ? 1 : 0;
          return aEnabled - bEnabled; // Enabled packs last
        case "name":
        default:
          return a.name.localeCompare(b.name);
      }
    });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2">
          <Library className="h-4 w-4" />
          Convention Packs
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-4xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Library className="h-5 w-5" />
            Convention Pack Library
          </DialogTitle>
          <p className="text-sm text-muted-foreground">
            Enable pre-built convention packs for {scopeLabel}
          </p>
        </DialogHeader>

        <div className="space-y-3 border-b pb-4">
          <div className="flex gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search packs by name, description, or conventions..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {Object.entries(CATEGORY_CONFIG).map(([key, config]) => {
                  const Icon = config.icon;
                  return (
                    <SelectItem key={key} value={key}>
                      <div className="flex items-center gap-2">
                        <Icon className="h-4 w-4" style={{ color: config.color }} />
                        {config.label}
                      </div>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
            <Select value={sortBy} onValueChange={(value: any) => setSortBy(value)}>
              <SelectTrigger className="w-[160px]">
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="favorites">
                  <div className="flex items-center gap-2">
                    <Star className="h-4 w-4" />
                    Favorites First
                  </div>
                </SelectItem>
                <SelectItem value="name">
                  <div className="flex items-center gap-2">
                    <ArrowUpDown className="h-4 w-4" />
                    Name
                  </div>
                </SelectItem>
                <SelectItem value="conventions">
                  <div className="flex items-center gap-2">
                    <ArrowUpDown className="h-4 w-4" />
                    # Conventions
                  </div>
                </SelectItem>
                <SelectItem value="status">
                  <div className="flex items-center gap-2">
                    <ArrowUpDown className="h-4 w-4" />
                    Status
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center justify-between text-sm text-muted-foreground">
            <span>Showing {filteredAndSortedPacks.length} of {packs.length} packs</span>
            <div className="flex items-center gap-2">
              {favorites.size > 0 && (
                <span className="flex items-center gap-1">
                  <Star className="h-3 w-3 fill-primary text-primary" />
                  {favorites.size} favorited
                </span>
              )}
              {searchQuery && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 px-2"
                  onClick={() => setSearchQuery("")}
                >
                  Clear search
                </Button>
              )}
            </div>
          </div>
        </div>

        <ScrollArea className="flex-1 pr-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : filteredAndSortedPacks.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <p className="font-medium">No convention packs found</p>
              {searchQuery && (
                <p className="text-sm mt-2">Try adjusting your search or filters</p>
              )}
            </div>
          ) : (
            <div className="grid gap-4">
              {filteredAndSortedPacks.map((pack) => {
                const categoryConfig = CATEGORY_CONFIG[pack.category as keyof typeof CATEGORY_CONFIG];
                const CategoryIcon = categoryConfig?.icon || Library;
                const packEnabled = isPackEnabled(pack);
                const status = getPackStatus(pack);
                const isFavorited = favorites.has(pack.id);

                return (
                  <Card key={pack.id} className={packEnabled ? "border-primary/50" : isFavorited ? "border-primary/30" : ""}>
                    <CardHeader>
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <CardTitle className="text-lg">{pack.name}</CardTitle>
                            {isFavorited && (
                              <Badge variant="outline" className="gap-1 border-primary/50 text-primary">
                                <Star className="h-3 w-3 fill-primary" />
                                Favorite
                              </Badge>
                            )}
                            {packEnabled && (
                              <Badge variant="default" className="gap-1">
                                <CheckCircle2 className="h-3 w-3" />
                                Enabled
                              </Badge>
                            )}
                          </div>
                          <CardDescription className="mt-1">
                            {pack.description}
                          </CardDescription>
                          <div className="flex items-center gap-2 mt-2">
                            <Badge
                              variant="outline"
                              style={{
                                borderColor: categoryConfig?.color,
                                color: categoryConfig?.color,
                              }}
                            >
                              <CategoryIcon className="h-3 w-3 mr-1" />
                              {categoryConfig?.label}
                            </Badge>
                            <Badge variant="secondary">
                              {pack.conventions.length} conventions
                            </Badge>
                            {status.enabled > 0 && !packEnabled && (
                              <Badge variant="outline" className="text-xs">
                                {status.enabled}/{status.total} enabled
                              </Badge>
                            )}
                            {pack.is_system && (
                              <Badge variant="outline">Official</Badge>
                            )}
                           </div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            onClick={(e) => toggleFavorite(pack.id, e)}
                            disabled={togglingFavorite === pack.id}
                            size="sm"
                            variant="ghost"
                            className="gap-2"
                          >
                            {togglingFavorite === pack.id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Star className={`h-4 w-4 ${isFavorited ? "fill-primary text-primary" : ""}`} />
                            )}
                          </Button>
                          <Button
                            onClick={() => handleEnablePack(pack)}
                            disabled={enabling === pack.id || packEnabled}
                            size="sm"
                            variant={packEnabled ? "outline" : "default"}
                          >
                          {enabling === pack.id ? (
                            <>
                              <Loader2 className="h-4 w-4 animate-spin mr-2" />
                              Enabling...
                            </>
                          ) : packEnabled ? (
                            <>
                              <CheckCircle2 className="h-4 w-4 mr-2" />
                              Enabled
                            </>
                          ) : status.enabled > 0 ? (
                            <>
                              <Plus className="h-4 w-4 mr-2" />
                              Add Missing
                            </>
                          ) : (
                            <>
                              <Plus className="h-4 w-4 mr-2" />
                              Enable
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                    </CardHeader>
                    <CardContent>
                      <Collapsible 
                        open={expandedPacks.has(pack.id)}
                        onOpenChange={() => togglePackExpansion(pack.id)}
                      >
                        <CollapsibleTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="w-full justify-between hover:bg-muted/50"
                          >
                            <span className="text-sm font-medium">
                              {expandedPacks.has(pack.id) ? "Hide" : "Show"} conventions
                            </span>
                            {expandedPacks.has(pack.id) ? (
                              <ChevronUp className="h-4 w-4" />
                            ) : (
                              <ChevronDown className="h-4 w-4" />
                            )}
                          </Button>
                        </CollapsibleTrigger>
                        <CollapsibleContent className="mt-3">
                          <ul className="space-y-1">
                            {pack.conventions.map((convention, idx) => {
                              const isEnabled = existingMemories.includes(convention.trim().toLowerCase());
                              return (
                                <li key={idx} className="text-sm flex items-start gap-2">
                                  <Check className={`h-4 w-4 mt-0.5 shrink-0 ${
                                    isEnabled ? "text-primary" : "text-muted-foreground"
                                  }`} />
                                  <span className={isEnabled ? "text-muted-foreground" : ""}>
                                    {convention}
                                  </span>
                                </li>
                              );
                            })}
                          </ul>
                        </CollapsibleContent>
                      </Collapsible>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};
